import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Session, User } from "@supabase/supabase-js";
import { maybeRecoverGoogleOAuthReturn } from "@/lib/oauthReturn";

interface AuthCtx {
  user: User | null;
  session: Session | null;
  loading: boolean;
}

const Ctx = createContext<AuthCtx>({ user: null, session: null, loading: true });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // True while a one-time clean-load navigation is under way: keep rendering
  // the loading state instead of the authenticated app.
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    const apply = (s: Session | null) => {
      // A surviving `tl-google-oauth-pending` marker plus a valid session means
      // this document is a fresh Google OAuth return, whatever route the hosted
      // broker landed on. Recovery replaces the document once.
      if (maybeRecoverGoogleOAuthReturn(!!s)) {
        setRecovering(true);
        return;
      }
      setSession(s);
      setLoading(false);
    };
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => apply(s));
    supabase.auth.getSession().then(({ data }) => apply(data.session));
    return () => sub.subscription.unsubscribe();
  }, []);

  return (
    <Ctx.Provider value={{ user: session?.user ?? null, session, loading: loading || recovering }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
